import { call, put, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import {
  fetchProdSeals,
  fetchProdSealsStart,
  fetchProdSealsSuccess,
  fetchProdSealsFailure,
  createProdSeal,
  createProdSealStart,
  createProdSealSuccess,
  createProdSealFailure,
  customizeProdseal,
  customizeProdsealStart,
  ProdsealCustomizationPayload,
  customizeProdsealSuccess,
  customizeProdsealFailure,
  fetchProdSealById,
  fetchProdSealByIdStart,
  fetchProdSealByIdSuccess,
  fetchProdSealByIdFailure,
  downloadProdSealsPdf,
  downloadProdSealsPdfStart,
  downloadProdSealsPdfSuccess,
  downloadProdSealsPdfFailure,
  previewProdSealsPdf,
  previewProdSealsPdfStart,
  previewProdSealsPdfSuccess,
  previewProdSealsPdfFailure,
  PdfLayoutConfig,
} from "../slices/prodsealsSlice";
import api, { makeRequest } from "../../utils/api";
import { extractErrorMessage, extractErrorDetails } from "../../utils/helpers";
import { toast } from "react-toastify";
import { CreateProdSealRequest, ProdSeal } from "../../types/pages/prodseals";

function* fetchProdSealsSaga(
  action: PayloadAction<{
    page: number;
    rowsPerPage: number;
    batchId?: string;
    sortBy?: string;
    sortOrder?: number;
  }>,
): Generator<any, void, any> {
  try {
    yield put(fetchProdSealsStart());
    const skip = action.payload.page * action.payload.rowsPerPage;
    const limit = action.payload.rowsPerPage;
    const batchId = action.payload.batchId;
    const sortBy = action.payload.sortBy || "created_at";
    const sortOrder =
      action.payload.sortOrder !== undefined ? action.payload.sortOrder : -1;

    let endpoint = `/prodseals/?skip=${skip}&limit=${limit}&sort_by=${sortBy}&sort_order=${sortOrder}`;
    if (batchId) {
      endpoint += `&batch_id=${batchId}`;
    }

    const response = yield call(makeRequest, "get", endpoint);
    yield put(fetchProdSealsSuccess(response));
  } catch (error: any) {
    yield put(fetchProdSealsFailure(extractErrorMessage(error)));
  }
}

function* createProdSealSaga(
  action: PayloadAction<CreateProdSealRequest>,
): Generator<any, void, any> {
  try {
    yield put(createProdSealStart());
    const response = yield call(
      makeRequest,
      "post",
      "/prodseals/",
      action.payload,
    );
    yield put(createProdSealSuccess(response.data));
    toast.success(response.message || "ProdSeal generated successfully!");
  } catch (error: any) {
    const errorDetails = extractErrorDetails(error);
    const errorMessage = extractErrorMessage(error);

    if (
      errorDetails?.message ===
      "All prodseals for this batch have already been generated"
    ) {
      toast.error("All prodseals for this batch have already been generated!");
    } else {
      toast.error(errorMessage);
    }

    yield put(createProdSealFailure(errorMessage));
  }
}

function* customizeProdsealSaga(
  action: PayloadAction<ProdsealCustomizationPayload>,
) {
  try {
    yield put(customizeProdsealStart());
    const response: { image: string } = yield call(
      makeRequest,
      "post",
      "/prodseals/preview",
      action.payload,
    );
    yield put(customizeProdsealSuccess(response.image));
  } catch (error: any) {
    yield put(
      customizeProdsealFailure(error.message || "Failed to customize Prodseal"),
    );
  }
}

function* fetchProdSealByIdSaga(action: PayloadAction<string>) {
  try {
    yield put(fetchProdSealByIdStart());
    const response: ProdSeal = yield call(
      makeRequest,
      "get",
      `/prodseals/${action.payload}`,
    );
    yield put(fetchProdSealByIdSuccess(response));
  } catch (error: any) {
    yield put(fetchProdSealByIdFailure(extractErrorMessage(error)));
  }
}

function buildPrintQueryString(
  layout: PdfLayoutConfig,
  preview: boolean = false,
): string {
  const params = new URLSearchParams({
    label_width: layout.labelWidth.toString(),
    label_height: layout.labelHeight.toString(),
    columns: layout.columns.toString(),
    margin: layout.margin.toString(),
    row_spacing: layout.rowSpacing.toString(),
    column_spacing: layout.columnSpacing.toString(),
    page_size: layout.pageSize,
    ...(preview ? { preview: "true" } : {}),
  });
  return params.toString();
}

function* previewProdSealsPdfSaga(
  action: PayloadAction<{ batchId: string; layout: PdfLayoutConfig }>,
): Generator<any, void, any> {
  try {
    yield put(previewProdSealsPdfStart());
    const qs = buildPrintQueryString(action.payload.layout, true);
    const response = yield call(
      [api, api.get],
      `/prodseals/batch/${action.payload.batchId}/print?${qs}`,
      { responseType: "blob" },
    );
    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    yield put(previewProdSealsPdfSuccess(url));
  } catch (error: any) {
    const errorMessage = extractErrorMessage(error);
    toast.error(errorMessage || "Failed to generate preview");
    yield put(previewProdSealsPdfFailure(errorMessage));
  }
}

function* downloadProdSealsPdfSaga(
  action: PayloadAction<{
    batchId: string;
    batchNumber: string;
    layout: PdfLayoutConfig;
  }>,
): Generator<any, void, any> {
  try {
    yield put(downloadProdSealsPdfStart());
    const qs = buildPrintQueryString(action.payload.layout);
    const response = yield call(
      [api, api.get],
      `/prodseals/batch/${action.payload.batchId}/print?${qs}`,
      { responseType: "blob" },
    );
    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `prodseals-${action.payload.batchNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    yield put(downloadProdSealsPdfSuccess());
    toast.success("PDF downloaded successfully!");
  } catch (error: any) {
    const errorMessage = extractErrorMessage(error);
    toast.error(errorMessage || "Failed to download PDF");
    yield put(downloadProdSealsPdfFailure(errorMessage));
  }
}

export default function* prodsealsSaga(): Generator<any, void, any> {
  yield takeLatest(fetchProdSeals.type, fetchProdSealsSaga);
  yield takeLatest(createProdSeal.type, createProdSealSaga);
  yield takeLatest(customizeProdseal.type, customizeProdsealSaga);
  yield takeLatest(fetchProdSealById.type, fetchProdSealByIdSaga);
  yield takeLatest(downloadProdSealsPdf.type, downloadProdSealsPdfSaga);
  yield takeLatest(previewProdSealsPdf.type, previewProdSealsPdfSaga);
}
